import { Patient, CreatePatientData } from '../types/patient';
import { Appointment } from '../types/appointment';
import { apiService } from './api';

// Abstract interface for data operations
export interface DataSource {
  // Patient operations
  getPatients(params?: { page?: number; limit?: number; search?: string; isActive?: boolean }): Promise<{
    data: {
      patients: Patient[];
      pagination: {
        page: number;
        limit: number;
        total: number;
        pages: number;
      };
    };
  }>;
  getPatient(id: string): Promise<Patient>;
  createPatient(data: CreatePatientData): Promise<Patient>;
  updatePatient(id: string, data: Partial<Patient>): Promise<Patient>;
  deletePatient(id: string): Promise<void>;

  // Appointment operations
  getAppointments(params?: {
    page?: number;
    limit?: number;
    status?: string;
    providerId?: string;
  }): Promise<{
    appointments: Appointment[];
    total: number;
  }>;
  getAppointment(id: string): Promise<Appointment>;
  createAppointment(data: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>): Promise<Appointment>;
  updateAppointment(id: string, data: Partial<Appointment>): Promise<Appointment>;
  deleteAppointment(id: string): Promise<void>;
}

export interface OrganizationCapabilities {
  patients: {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  appointments: {
    read: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
  };
  mode: 'standalone' | 'connected';
  ehrStatus: 'active' | 'inactive' | 'error';
}

// Unified implementation - uses capabilities from backend
class UnifiedDataSource implements DataSource {
  private capabilities: OrganizationCapabilities | null = null;
  private capabilitiesPromise: Promise<OrganizationCapabilities> | null = null;

  private async getCapabilities(): Promise<OrganizationCapabilities> {
    if (this.capabilities) {
      return this.capabilities;
    }

    if (this.capabilitiesPromise) {
      return this.capabilitiesPromise;
    }

    this.capabilitiesPromise = apiService.get<OrganizationCapabilities>('/v1/organizations/capabilities')
      .then(capabilities => {
        this.capabilities = capabilities;
        return capabilities;
      });

    return this.capabilitiesPromise;
  }

  private async checkCapability(entity: 'patients' | 'appointments', operation: 'read' | 'create' | 'update' | 'delete'): Promise<void> {
    const capabilities = await this.getCapabilities();
    
    if (!capabilities[entity][operation]) {
      const action = operation === 'read' ? 'view' : operation;
      throw new Error(
        `Cannot ${action} ${entity} in ${capabilities.mode} mode. ${
          capabilities.mode === 'connected' 
            ? `${entity} must be managed in the EHR system.`
            : 'Operation not allowed.'
        }`
      );
    }
  }

  async getPatients(params?: { page?: number; limit?: number; search?: string; isActive?: boolean }): Promise<{ data: { patients: Patient[]; pagination: { page: number; limit: number; total: number; pages: number; } } }> {
    await this.checkCapability('patients', 'read');
    const capabilities = await this.getCapabilities();
    
    // Use appropriate endpoint based on mode
    const endpoint = capabilities.mode === 'connected' ? '/v1/ehr/patients' : '/v1/patients';
    return await apiService.get<{ data: { patients: Patient[]; pagination: { page: number; limit: number; total: number; pages: number; } } }>(endpoint, params);
  }

  async getPatient(id: string): Promise<Patient> {
    await this.checkCapability('patients', 'read');
    const capabilities = await this.getCapabilities();
    
    const endpoint = capabilities.mode === 'connected' ? `/v1/ehr/patients/${id}` : `/v1/patients/${id}`;
    const response = await apiService.get<{ success: boolean; data: { patient: Patient } }>(endpoint);
    return response.data.patient;
  }

  async createPatient(data: CreatePatientData): Promise<Patient> {
    await this.checkCapability('patients', 'create');
    
    const response = await apiService.post<{ success: boolean; data: { patient: Patient } }>('/v1/patients', data);
    return response.data.patient;
  }

  async updatePatient(id: string, data: Partial<Patient>): Promise<Patient> {
    await this.checkCapability('patients', 'update');

    const response = await apiService.put<{ success: boolean; data: { patient: Patient } }>(`/v1/patients/${id}`, data);
    return response.data.patient;
  }

  async deletePatient(id: string): Promise<void> {
    await this.checkCapability('patients', 'delete');
    await apiService.delete<void>(`/v1/patients/${id}`);
  }

  async getAppointments(params?: {
    page?: number;
    limit?: number;
    status?: string;
    providerId?: string;
  }): Promise<{ appointments: Appointment[]; total: number }> {
    await this.checkCapability('appointments', 'read');
    const capabilities = await this.getCapabilities();
    
    const endpoint = capabilities.mode === 'connected' ? '/v1/ehr/appointments' : '/v1/appointments';
    const response = await apiService.get<{ 
      success: boolean; 
      data: { 
        appointments: Appointment[]; 
        pagination: { page: number; limit: number; total: number; pages: number; }
      } 
    }>(endpoint, params);
    
    return {
      appointments: response.data.appointments,
      total: response.data.pagination.total
    };
  }

  async getAppointment(id: string): Promise<Appointment> {
    await this.checkCapability('appointments', 'read');
    const capabilities = await this.getCapabilities();
    
    const endpoint = capabilities.mode === 'connected' ? `/v1/ehr/appointments/${id}` : `/v1/appointments/${id}`;
    const response = await apiService.get<{ 
      success: boolean; 
      data: { appointment: Appointment } 
    }>(endpoint);
    
    return response.data.appointment;
  }

  async createAppointment(data: Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>): Promise<Appointment> {
    await this.checkCapability('appointments', 'create');
    
    const response = await apiService.post<{ success: boolean; data: { appointment: Appointment } }>('/v1/appointments', data);
    return response.data.appointment;
  }

  async updateAppointment(id: string, data: Partial<Appointment>): Promise<Appointment> {
    await this.checkCapability('appointments', 'update');
    const capabilities = await this.getCapabilities();
    
    if (capabilities.mode === 'connected') {
      // In connected mode, only allow recording/transcription updates
      const allowedFields = ['recordingStatus', 'transcriptionStatus', 'recordingUrl', 'transcriptId'];
      const filteredData: Partial<Appointment> = {};
      
      Object.keys(data).forEach(key => {
        if (allowedFields.includes(key)) {
          (filteredData as any)[key] = (data as any)[key];
        }
      });
      
      const response = await apiService.put<{ success: boolean; data: { appointment: Appointment } }>(`/v1/appointments/${id}`, filteredData);
      return response.data.appointment;
    } else {
      const response = await apiService.put<{ success: boolean; data: { appointment: Appointment } }>(`/v1/appointments/${id}`, data);
      return response.data.appointment;
    }
  }

  async deleteAppointment(id: string): Promise<void> {
    await this.checkCapability('appointments', 'delete');
    await apiService.delete<void>(`/v1/appointments/${id}`);
  }

  // Utility method to get current capabilities (for UI logic)
  async getCurrentCapabilities(): Promise<OrganizationCapabilities> {
    return await this.getCapabilities();
  }

  // Method to refresh capabilities (e.g., after organization settings change)
  refreshCapabilities(): void {
    this.capabilities = null;
    this.capabilitiesPromise = null;
  }
}

// Create singleton instance
const unifiedDataSourceInstance = new UnifiedDataSource();

// Export singleton instance
export const dataSource = unifiedDataSourceInstance;

// Legacy exports for backward compatibility (TEMPORARY - remove after migration)
export const patientService = unifiedDataSourceInstance;
export const getDataSource = () => unifiedDataSourceInstance;